import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DollarOutlined,
  FilePdfOutlined,
  LinkOutlined,
  MailOutlined,
  RollbackOutlined,
  UserOutlined
} from "@ant-design/icons"
import {
  Button,
  Dropdown,
  Flex,
  type MenuProps,
  Space,
  Tag,
  Typography
} from "antd"
import dayjs from "dayjs"
import React, { useMemo } from "react"
import { Link } from "react-router-dom"

import Icon from "../../../../Icon"
import { empty } from "../../../../constants/placeholders"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { getDeclineReasonForDisplay } from "../../../../utils/documents/document-decline-outcome.ts"
import { formatNumber } from "../../../../utils/number/formate.ts"
import { ALL_DATE_FORMATS } from "../../../TeamManagementPage/constants/date-formats.ts"
import {
  CONTRACT_STATUSES,
  CONTRACT_STATUSES_OBJ,
  type ContractStatuses,
  canShareContractByEmail
} from "../../constants/contract-statuses-keys.ts"
import { emailContractToClient } from "../../redux/contracts.thunks.ts"
import { type Contract } from "../../types/types.ts"
import { SignatureOriginTag } from "./SignatureOriginTag.tsx"

const { Title, Text } = Typography

interface ContractViewHeaderProps {
  contract: Contract
  saving: boolean
  viewLeadAllowed?: boolean
  onApproveContract: () => void
  onUnderRevision: () => void
  onCancelReview: () => void
  onDuplicateContract: () => void
}

export function ContractViewHeader({
  contract,
  viewLeadAllowed,
  saving,
  onApproveContract,
  onUnderRevision,
  onCancelReview,
  onDuplicateContract
}: ContractViewHeaderProps) {
  const dispatch = useAppDispatch()
  const company = contract?.company_data
  const submittedAttachment = contract?.submitted_attachment
  const version = submittedAttachment?.full_version || contract?.version_display
  const submittedBy = submittedAttachment?.uploaded_by_name
  const reviewedBy = contract?.reviewed_by_name
  const statusData = CONTRACT_STATUSES_OBJ?.[contract?.status]
  const date = contract?.reviewed_at || contract?.submitted_for_review_at

  const ALLOWED_STATUSES: ContractStatuses[] = [
    CONTRACT_STATUSES.PENDING_TO_REVIEW
  ]

  const isPendingReview = ALLOWED_STATUSES.includes(contract.status)
  const isDeclined = contract?.status === CONTRACT_STATUSES.DECLINED
  const isCancelled = contract?.status === CONTRACT_STATUSES.CANCELLED
  const isUnderRevisionOnly = isDeclined && !isCancelled
  const isUploadedFile = submittedAttachment?.extra?.uploaded

  const displayReason = getDeclineReasonForDisplay(contract?.decline_reason)

  const contractMeta = [
    {
      icon: <UserOutlined />,
      label: reviewedBy ? "Reviewed by" : "Submitted by",
      value: reviewedBy || submittedBy || empty,
      bold: true
    },
    {
      icon: <CalendarOutlined />,
      value: date || empty
    },
    {
      icon: <ClockCircleOutlined />,
      value: date ? dayjs(date, ALL_DATE_FORMATS)?.format("h:mm A") : empty
    },
    {
      icon: <FilePdfOutlined />,
      label: "Version",
      value: version
    },
    {
      icon: <DollarOutlined />,
      value: formatNumber(contract?.total_amount),
      bold: true
    }
  ]

  const kebabMenuItems: MenuProps["items"] = useMemo(() => {
    const list = []

    if (canShareContractByEmail(contract ?? {})) {
      list.push({
        key: "1",
        label: "Share via Email",
        onClick: () => dispatch(emailContractToClient(contract.id)),
        icon: <MailOutlined />
      })
    }

    if (viewLeadAllowed && contract?.lead_id) {
      list.push({
        key: "2",
        label: (
          <Link target={"_blank"} to={RoutesPaths.lead.view(contract.lead_id)}>
            Open Lead
          </Link>
        ),
        icon: <LinkOutlined />
      })
    }

    return list
  }, [dispatch, viewLeadAllowed, contract])

  return (
    <Flex gap={"small"} vertical>
      <Flex wrap justify={"space-between"} align={"center"} gap={"middle"}>
        <Flex vertical>
          <Flex gap={"small"} align={"center"} wrap>
            <Title level={4}>
              {contract?.company_data?.company_name || "No company name"}
            </Title>
            <Tag style={statusData?.style}>
              {statusData?.label || contract?.status}
            </Tag>
            <SignatureOriginTag origin={contract?.signature_origin} />
          </Flex>

          <Flex wrap align={"center"} gap={3}>
            <Text type={"secondary"}>
              {company?.company_name || "No company name"}
            </Text>
            <span>&bull;</span>
            <Text type={"secondary"}>Contract ID: {contract?.contract_id}</Text>
          </Flex>
          {(isDeclined || isPendingReview) && displayReason ? (
            <Text type="secondary" className="mt-1 max-w-2xl">
              <Text strong>Reason: </Text>
              {displayReason}
            </Text>
          ) : null}
        </Flex>

        <Space wrap>
          {isPendingReview && (
            <>
              <Button
                color={"green"}
                variant={"solid"}
                icon={<CheckOutlined />}
                disabled={saving}
                onClick={onApproveContract}
              >
                Approve
              </Button>
              <Button
                color={"orange"}
                variant={"solid"}
                icon={<RollbackOutlined />}
                disabled={saving}
                onClick={onUnderRevision}
              >
                Under Revision
              </Button>
              <Button
                icon={<CloseCircleOutlined />}
                danger
                color={"danger"}
                variant={"solid"}
                disabled={saving}
                onClick={onCancelReview}
              >
                Cancel
              </Button>
            </>
          )}
          {isUnderRevisionOnly && !isUploadedFile && (
            <Button
              icon={<CopyOutlined />}
              variant={"solid"}
              disabled={saving}
              onClick={onDuplicateContract}
            >
              Duplicate
            </Button>
          )}
          {kebabMenuItems?.length > 0 ? (
            <Dropdown
              disabled={saving}
              menu={{ items: kebabMenuItems }}
              trigger={["click"]}
            >
              <Button
                variant={"solid"}
                onClick={(e) => e.stopPropagation()}
                icon={<Icon type={"VerticalDots"} />}
              />
            </Dropdown>
          ) : null}
        </Space>
      </Flex>
      <Space size="large" wrap className="text-gray-600">
        {contractMeta.map((item, idx) => (
          <Space key={idx} size={4}>
            {item.icon}
            {item.label && <Text type="secondary">{item.label}</Text>}
            <Text strong={item.bold}>{item.value}</Text>
          </Space>
        ))}
      </Space>
    </Flex>
  )
}
